/**
 * AD-021's two encounter-level operations: putting the pilot over the side, and
 * advancing the canopy he comes down under.
 *
 * Its own file rather than more of `encounter.ts` for the reason
 * `encounter-settle.ts` was split out of `encounter-tick.ts`
 * (`lessons.md`, 2026-08-26): `encounter.ts` was five lines from the size
 * ceiling before this arrived, and the ceiling is not compositional. The split
 * is a real seam and not only a size one — neither function is part of the tick
 * pipeline, and `encounter.ts` does not import this back.
 *
 * The descent physics is `parachute.ts`; the whole design is
 * `docs/engineering/bail-out-design.md`.
 */

import type { GroundQuery } from "../flight/ground.ts";
import type { SimState } from "../flight/state.ts";
import type { CombatAircraft, EncounterState } from "./encounter.ts";
import { beginJump, stepParachute, type ParachuteState } from "./parachute.ts";

/**
 * Whether *this pilot's own state* permits the jump — everything `bailOut`
 * refuses on except the roster question of whether the aeroplane exists at all.
 *
 * Exported because G38 put a **BAIL OUT row on the in-flight `ESC` dialog**, and
 * a row you can click that does nothing is worse than no row: the dialog has to
 * know before it draws itself. That is a second reader of rules `bailOut` owns,
 * and the only safe way to have one is for both to read the same function —
 * `scene-loop.ts`'s `advanceBailOut` says exactly why in its own comment
 * (*"Nothing here decides whether the pilot may go — that would be a second copy
 * of rules the engine already owns"*). The `H` hold still tests nothing and still
 * fires blind; this exists for the affordance, not for the command.
 *
 * The roster half stays in `bailOut`, because a `SimState` cannot answer it.
 * A caller that has only the player's `SimState` — `hud-snapshot.ts` — therefore
 * gets a predicate that is right about the pilot and silent about a destroyed
 * airframe; the residual case (an aeroplane already `destroyed` whose pilot is
 * still alive, unhurt and airborne) cannot arise while the dialog is reachable,
 * since the sortie is ending by then.
 */
export function pilotMayBailOut(sim: SimState): boolean {
  if (!sim.pilot.alive || sim.pilot.bailedOut || sim.crashed) return false;
  return sim.readout.altitudeAglM > 0 && !sim.gear.onGround;
}

/**
 * The bail-out command.
 *
 * **A command, not an input.** `applyWound` and `killPilot` are the precedent:
 * things that happen *to* a pilot from outside the tick are exported functions
 * that stamp state, not fields on `FlightInputs`. Doing it this way also keeps
 * `packages/net`'s wire untouched — `EncodedInputs` is a five-integer tuple with
 * exactly three flag bits, so a `FlightInputs.bailOut` would have cost a fourth
 * bit and a `NET_PROTOCOL_VERSION` bump for a verb `/duel` does not offer.
 *
 * Returns the state **unchanged** when the jump is not available, so a caller
 * may fire it on a held key without testing anything first. The four refusals:
 *
 * - there is no such aeroplane, or it is already destroyed — nothing to jump
 *   out of;
 * - the pilot is dead — a corpse does not climb over the side;
 * - he has already jumped — the seat is empty;
 * - **he is not airborne.** Bailing out of a taxiing aeroplane is not a thing,
 *   and without this the verb would be a way to abandon a sortie on the
 *   aerodrome and be scored as having landed safely away from it.
 *
 * That last one is asked as a **height** and not as `readout.grounded` or
 * `gear.onGround`, both of which are computed by the tick and are therefore
 * still `false` on a freshly created aeroplane sitting on the aerodrome. A flag
 * that lags the world by one tick is a cached answer, not the question.
 */
export function bailOut(state: EncounterState, pilotId: string): EncounterState {
  const entry = state.aircraft.find((candidate) => candidate.id === pilotId);
  if (!entry || entry.status === "destroyed") return state;
  const { sim } = entry;
  if (!pilotMayBailOut(sim)) return state;
  const jumped: CombatAircraft = { ...entry, sim: { ...sim, pilot: { ...sim.pilot, bailedOut: true } } };
  return {
    ...state,
    aircraft: state.aircraft.map((candidate) => (candidate.id === pilotId ? jumped : candidate)),
    parachute: beginJump({ pilotId, sim }),
  };
}

/**
 * One tick of the canopy, or `undefined` when nobody is under one.
 *
 * Called from `stepEncounter` rather than from inside `advanceRoster` for two
 * reasons: a descending pilot is not on the roster and has no `SimState`, so
 * none of the roster passes have anything to say about him; and
 * `encounter-tick.ts` has no size headroom left either.
 *
 * He samples the **same `GroundQuery` every aeroplane does**, which is the rule
 * that stops two subsystems disagreeing about where the ground is — the fault
 * class this codebase has written up four times.
 */
export function advanceParachute(
  parachute: ParachuteState | undefined,
  ground: GroundQuery,
  dt: number,
): ParachuteState | undefined {
  if (!parachute) return undefined;
  const { x, z } = parachute.position;
  return stepParachute(parachute, { dt, groundHeightM: ground.groundHeightAt(x, z) });
}
